import React, { ReactNode, useContext } from 'react';
import styled from 'styled-components';

import { FatalAttraction } from '@vpr/tracking';

import { rem } from '@vimeo/bokeh';
import { bokehTheme as theme } from '@vimeo/bokeh-theme';
import { translate } from '@vimeo/i18n-runtime';
import { Paragraph } from '@vimeo/iris/typography';
import { ViewerContext } from '@vimeo/viewer';

import ExpandableList from '../../ExpandableList';
import Logo from '../../Logo';
import { Copyright } from '../Copyright';
import DoNotSellLink from '../DoNotSellLink';
import * as Links from '../Links';

const Footer = styled.footer`
  display: flex;
  flex-direction: column;
  background: ${theme.colors.gray['800']};

  @media (min-width: ${rem(1000)}) {
    display: none;
  }
`;

const Section = styled.div`
  margin: 0 ${rem(24)};
  padding: ${rem(12)} 0;

  p {
    color: ${theme.colors.white};
  }
`;

const Text = styled(Paragraph).attrs({ size: '1' })`
  color: ${theme.colors.gray['800']};
  margin: 0;
`;

interface Props {
  absoluteUrl?: boolean;
  languageSelect: ReactNode;
  isDoNotSellReady?: boolean;
}

function MobileStandardFooter({ absoluteUrl = false, languageSelect, isDoNotSellReady }: Props) {
  const viewer = useContext(ViewerContext);
  const isSimplifiedSite = viewer?.isSimplifiedSite;
  const impressumQualifies = viewer?.impressumQualifies;
  const terminateContractQualifies = viewer?.terminateContractQualifies;

  const siteMapLinks = Links.getSiteMapLinks(!!terminateContractQualifies);

  const getLegalItems = () => {
    const items = Links.getLegalLinks()
      .filter(
        (link) =>
          (impressumQualifies ? true : link.title !== 'Impressum') &&
          (!isSimplifiedSite || link.forSimplifiedSite === isSimplifiedSite),
      )
      .map((link, index) => (
        <Links.Link
          key={`${link}-mobile-${index}`}
          title={link.title}
          href={absoluteUrl ? Links.getAbsoluteUrl(link.href) : link.href}
        >
          {link.title}
        </Links.Link>
      ));
    // Inserting Do Not Sell link after Privacy
    items.splice(
      2,
      0,
      <DoNotSellLink footerType="standardMobile" isDoNotSellReady={isDoNotSellReady} />,
    );
    return items;
  };

  return (
    <Footer>
      <Section>
        <a
          href="/"
          aria-label={translate('Vimeo logo. Click on this link to navigate to the homepage.')}
        >
          <Logo color={theme.colors.white as string} width={rem(120)} />
        </a>
      </Section>
      {siteMapLinks.map((section, index: number) => {
        return (
          <ExpandableList
            location="footer"
            key={`mobile-footer-links-${section.header}-${index}`}
            title={section.header}
            items={section.links.map((link, index) => {
              return (
                link && (
                  <Links.Link
                    key={`${link}-mobile-${index}`}
                    href={absoluteUrl ? Links.getAbsoluteUrl(link.href) : link.href}
                    onClick={() => {
                      if (link.fa) {
                        FatalAttraction.trackClick(link.fa);
                      }
                    }}
                  >
                    {link.label}
                  </Links.Link>
                )
              );
            })}
          />
        );
      })}
      <ExpandableList location="footer" title={translate('Legal')} items={getLegalItems()} />
      <Section>
        <Text>{translate('Language')}</Text>
        {languageSelect}
      </Section>
      <Section>
        <Copyright />
      </Section>
    </Footer>
  );
}

export default MobileStandardFooter;
